import { useCallback, useMemo } from 'react';
import axios, { isAxiosError } from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProductSizeInterface } from '../../../../../../Interface/ProductSize';
import type { VtsBasketInterface } from '../../../../../../Interface/Vts';
import type { CustomerDashboardPayload } from '../../../../Index/CustomerDashboard/types';

/** Shared with the basket drawer (VtsBasketContent), so adding here refreshes it too. */
export const VTS_BASKET_QUERY_KEY = ['/api/onlineshop/vts-basket'];

/** The shop's default product sort; the panel sorts and filters on the client. */
const DEFAULT_SORT = 'available';
/** Statuses of a customer dashboard that is not enabled or not available for the user. */
const DASHBOARD_UNAVAILABLE_STATUSES = [403, 503];
const REQUEST_HEADERS = {
  Accept: 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};

export interface BoothProducts {
  /** The exhibitor's items in API order. */
  items: ProductSizeInterface[];
  isLoading: boolean;
  isError: boolean;
  /** ERP ids of the items (sizes) the customer stocks; null when unknown. */
  stockedErpIds: Set<number> | null;
  /** Units in the VTS basket per flavour (shop id of the product attribute). */
  basketUnitsByAttributeId: Map<number, number>;
  refetchBasket: () => Promise<void>;
}

async function fetchProducts(
  exhibitorId: number
): Promise<ProductSizeInterface[]> {
  const params = new URLSearchParams();
  params.append('context[]', 'vts');
  params.append('context[]', 'product_size_list');
  params.append('context[]', 'product_attributes');
  params.append('exhibitor_id', String(exhibitorId));
  params.append('sortBy', DEFAULT_SORT);
  const response = await axios.get<ProductSizeInterface[]>(
    '/api/onlineshop/product-sizes?' + params.toString(),
    { headers: REQUEST_HEADERS }
  );

  return Array.isArray(response.data) ? response.data : [];
}

async function fetchBasket(): Promise<VtsBasketInterface> {
  const response = await axios.get<VtsBasketInterface>(
    '/api/onlineshop/vts-basket',
    { headers: REQUEST_HEADERS }
  );

  return response.data;
}

/**
 * The ERP ids of the items the customer stocks. A dashboard that is not enabled (403) or not
 * available (503) is an expected state, not an error: it resolves to null and shows no badges.
 */
async function fetchStockedErpIds(): Promise<number[] | null> {
  try {
    const response = await axios.get<CustomerDashboardPayload>(
      '/api/onlineshop/customer-dashboard',
      { headers: REQUEST_HEADERS }
    );
    const ids = response.data?.stocked_product_size_erp_ids;

    return Array.isArray(ids) ? ids : null;
  } catch (error) {
    if (
      isAxiosError(error) &&
      DASHBOARD_UNAVAILABLE_STATUSES.includes(error.response?.status ?? 0)
    ) {
      return null;
    }
    throw error;
  }
}

/** Loads the assortment of a booth, the VTS basket and the items the customer stocks. */
export default function useBoothProducts(exhibitorId: number): BoothProducts {
  const queryClient = useQueryClient();

  const productsQuery = useQuery({
    queryKey: ['vts-booth-assortment', exhibitorId],
    queryFn: () => fetchProducts(exhibitorId),
    staleTime: 60 * 1000,
  });
  const basketQuery = useQuery({
    queryKey: VTS_BASKET_QUERY_KEY,
    queryFn: fetchBasket,
  });
  const stockedQuery = useQuery({
    queryKey: ['vts-booth-assortment', 'stocked-product-size-erp-ids'],
    queryFn: fetchStockedErpIds,
    staleTime: 5 * 60 * 1000,
  });

  const items = useMemo(() => productsQuery.data ?? [], [productsQuery.data]);

  const stockedErpIds = useMemo(
    () => (stockedQuery.data ? new Set(stockedQuery.data) : null),
    [stockedQuery.data]
  );

  const basketUnitsByAttributeId = useMemo(() => {
    const units = new Map<number, number>();
    basketQuery.data?.positions?.forEach((position) => {
      const attributeId = position.product_attribute?.id;
      if (typeof attributeId === 'number' && position.quantity > 0) {
        units.set(
          attributeId,
          (units.get(attributeId) ?? 0) + position.quantity
        );
      }
    });

    return units;
  }, [basketQuery.data]);

  const refetchBasket = useCallback(
    () => queryClient.invalidateQueries({ queryKey: VTS_BASKET_QUERY_KEY }),
    [queryClient]
  );

  return {
    items,
    isLoading: productsQuery.isPending,
    isError: productsQuery.isError,
    stockedErpIds,
    basketUnitsByAttributeId,
    refetchBasket,
  };
}
